/**
 * Derive a human-readable tab title from a URL path.
 *
 * The destinations catalog (`shared/navigation/destinations.ts`) is the one
 * source of names for places a person can go to on purpose; this is the
 * fallback beneath it for routes the catalog does not (yet) list. It
 * doesn't need to be exhaustive — unknown paths get a reasonable fallback
 * derived from the last path segment.
 */

import { destinationForPath } from '@shared/navigation/destinations';

/**
 * Prefix-based fallback: /my-data/unlisted → "My Data"
 * Checks progressively shorter prefixes.
 */
const PREFIX_TITLES: [string, string][] = [
  ['/financial/', 'Financial'],
  ['/my-data/', 'My Data'],
];

/** Map a URL path to a human-readable tab title. */
export function titleForPath(path: string): string {
  // The catalog is the one source of names; the prefix table below covers routes
  // it does not (yet) list. Query and hash are stripped by destinationForPath.
  const dest = destinationForPath(path);
  if (dest) return dest.label;

  // Prefix match
  for (const [prefix, title] of PREFIX_TITLES) {
    if (path.startsWith(prefix)) return title;
  }

  // Fallback: capitalize the last path segment
  const segments = path.split('/').filter(Boolean);
  const last = segments[segments.length - 1] ?? 'Page';
  return last.charAt(0).toUpperCase() + last.slice(1).replace(/-/g, ' ');
}
